import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { enrollPractitioner } from 'api/Prescribe';
import { getPractitionerLicenses, upsertPractitionerLicense } from 'api/Practitioner';

import { useCustomMutation } from 'lib/hooks/useCustomMutation';

import { showAlert } from 'components/shared/Alert/Alert';

import useLicenseFormik from '../../License/lib/useLicenseFormik';

const PAGE_SIZE = 10;
const MIN_ITEMS_FOR_CONTROLS = 10;

export default function useEnrollmentModal({
  isOpen,
  provider,
  practiceId,
  onEnrollSuccess,
  handleClose
}) {
  const navigate = useNavigate();
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [showAddLicense, setShowAddLicense] = useState(false);
  const [showEditLicense, setShowEditLicense] = useState(false);
  const [editingLicense, setEditingLicense] = useState(null);
  const [licenses, setLicenses] = useState([]);
  const [loadingLicenses, setLoadingLicenses] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchLicenses = useCallback(() => {
    if (provider?.id) {
      setLoadingLicenses(true);
      getPractitionerLicenses(navigate, { practitionerId: provider.id })
        .then((res) => {
          const fetchedLicenses = res?.licenses || [];
          setLicenses(fetchedLicenses);
        })
        .catch(() => {
          showAlert({
            color: 'danger',
            title: "Couldn't load licenses. Please try again."
          });
        })
        .finally(() => setLoadingLicenses(false));
    }
  }, [navigate, provider?.id]);

  useEffect(() => {
    if (isOpen && provider?.id) {
      setSelectedLicense(null);
      setShowEditLicense(false);
      setEditingLicense(null);
      setCurrentPage(1);
      fetchLicenses();
    }
  }, [isOpen, provider?.id, fetchLicenses]);

  const totalItems = licenses.length;
  const showPagination = totalItems > MIN_ITEMS_FOR_CONTROLS;

  const paginatedLicenses = useMemo(() => {
    if (!showPagination) return licenses;
    const start = (currentPage - 1) * PAGE_SIZE;
    return licenses.slice(start, start + PAGE_SIZE);
  }, [licenses, currentPage, showPagination]);

  const enrollMutation = useCustomMutation({
    mutationFn: () =>
      enrollPractitioner(navigate, {
        userId: provider?.id,
        practiceId: Number(practiceId),
        licenseId: selectedLicense?.id
      }),
    onSuccess: (res) => {
      if (res?.requirements?.length > 0) {
        showAlert({ color: 'danger', title: res?.requirements?.[0] });
      } else {
        showAlert({ color: 'success', title: 'Provider enrolled' });
        onEnrollSuccess?.();
        handleClose();
      }
    },
    invalidateQueryKey: ['enrolled-practitioners'],
    disableAlert: true
  });

  const upsertLicenseMutation = useCustomMutation({
    mutationFn: (data) => upsertPractitionerLicense(navigate, data),
    onSuccess: (res) => {
      const newLicense = res?.license || res?.data?.license;
      if (newLicense) {
        setSelectedLicense(newLicense);
      }
      // Go back to license selection and refetch
      setShowAddLicense(false);
      setShowEditLicense(false);
      setEditingLicense(null);
      fetchLicenses();
    },
    successMessage: 'License added'
  });

  const formik = useLicenseFormik(async (values) => {
    await upsertLicenseMutation.mutateAsync({
      practitionerId: provider?.id,
      license: {
        ...values,
        practitioner_id: provider?.id,
        ...(editingLicense?.id ? { id: editingLicense.id } : {})
      }
    });
    formik.resetForm();
    setEditingLicense(null);
  });

  const handleEditLicense = useCallback(
    (license) => {
      setEditingLicense(license);
      setShowEditLicense(true);
      setShowAddLicense(true);
      formik.setValues({
        issuer_id: license.issuer_id,
        license_number: license.license_number,
        expired_on: license.expired_on,
        kind: license.kind || 'StateLicense',
        license_id: license.license_id,
        state_id: license.state_id
      });
    },
    [formik]
  );

  const handleEnroll = useCallback(() => {
    if (!selectedLicense) {
      showAlert({ color: 'warning', title: 'Select a license to continue' });
      return;
    }
    enrollMutation.mutate();
  }, [selectedLicense, enrollMutation]);

  return {
    selectedLicense,
    setSelectedLicense,
    showAddLicense,
    setShowAddLicense,
    showEditLicense,
    setShowEditLicense,
    editingLicense,
    licenses,
    loadingLicenses,
    currentPage,
    setCurrentPage,
    totalItems,
    showPagination,
    paginatedLicenses,
    PAGE_SIZE,
    enrollMutation,
    upsertLicenseMutation,
    formik,
    handleEnroll,
    handleEditLicense
  };
}
